import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { shippingService } from '../services/shippingService';
import { fieldClass, formatMoney, labelClass, parseNumber } from '../utils/form';
import { NumberField } from '../components/NumberField';
import { rowNumber } from '../utils/rowNumber';

type ShippingRow = {
  id: string;
  slug: string;
  name: string;
  description: string;
  eta: string;
  cost: number;
  isFree: boolean;
  freeThreshold: number | null;
  isActive: boolean;
  showInCheckout: boolean;
  sortOrder: number;
};

type FormState = {
  slug: string;
  name: string;
  description: string;
  eta: string;
  cost: number;
  isFree: boolean;
  freeThreshold: number;
  isActive: boolean;
  showInCheckout: boolean;
  sortOrder: number;
};

const emptyForm = (): FormState => ({
  slug: '',
  name: '',
  description: '',
  eta: '',
  cost: 0,
  isFree: false,
  freeThreshold: 0,
  isActive: true,
  showInCheckout: true,
  sortOrder: 0,
});

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  const n = parseNumber(String(value ?? ''));
  return Number.isFinite(n) ? n : fallback;
}

function mapRow(raw: Record<string, unknown>): ShippingRow {
  const thresh = raw.freeThreshold;
  return {
    id: String(raw.id ?? ''),
    slug: String(raw.slug ?? ''),
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    eta: String(raw.eta ?? ''),
    cost: num(raw.cost),
    isFree: Boolean(raw.isFree),
    freeThreshold: thresh == null || thresh === '' ? null : num(thresh),
    isActive: Boolean(raw.isActive),
    showInCheckout: raw.showInCheckout !== false,
    sortOrder: num(raw.sortOrder ?? raw.order),
  };
}

function formFromRow(row: ShippingRow): FormState {
  return {
    slug: row.slug,
    name: row.name,
    description: row.description,
    eta: row.eta,
    cost: row.cost,
    isFree: row.isFree,
    freeThreshold: row.freeThreshold ?? 0,
    isActive: row.isActive,
    showInCheckout: row.showInCheckout,
    sortOrder: row.sortOrder,
  };
}

function payloadFromForm(form: FormState): Record<string, unknown> {
  return {
    slug: form.slug.trim(),
    name: form.name.trim(),
    description: form.description.trim(),
    eta: form.eta.trim(),
    cost: form.isFree ? 0 : num(form.cost),
    isFree: form.isFree,
    freeThreshold: form.isFree || form.freeThreshold <= 0 ? null : num(form.freeThreshold),
    isActive: form.isActive,
    showInCheckout: form.showInCheckout,
    sortOrder: num(form.sortOrder),
  };
}

function priceLabel(row: ShippingRow): string {
  if (row.isFree) return 'همیشه رایگان';
  if (row.freeThreshold && row.freeThreshold > 0) {
    return `${formatMoney(row.cost)} تومان · رایگان از ${formatMoney(row.freeThreshold)}`;
  }
  return `${formatMoney(row.cost)} تومان`;
}

export function ShippingPage() {
  const [items, setItems] = useState<ShippingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ShippingRow | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await shippingService.list();
      setItems(data.map(mapRow));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت روش‌های ارسال');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row: ShippingRow) => {
    setEditing(row);
    setForm(formFromRow(row));
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('نام روش ارسال الزامی است');
      return;
    }
    if (!form.eta.trim()) {
      toast.error('زمان تحویل الزامی است');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing) {
        await shippingService.update(editing.id, payload);
        toast.success('روش ارسال به‌روزرسانی شد');
      } else {
        await shippingService.create(payload);
        toast.success('روش ارسال ایجاد شد');
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: ShippingRow) => {
    if (!window.confirm(`روش «${row.name}» حذف شود؟`)) return;
    try {
      await shippingService.remove(row.id);
      toast.success('حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">نحوه ارسال</h1>
          <p className="text-slate-600 mt-1">روش‌های ارسال، هزینه، زمان تحویل و ارسال رایگان</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن روش ارسال
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
          در حال بارگذاری...
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          هنوز روش ارسالی ثبت نشده.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="admin-table-scroll">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-medium">ردیف</th>
                <th className="px-4 py-3 text-right font-medium">روش</th>
                <th className="px-4 py-3 text-right font-medium">زمان تحویل</th>
                <th className="px-4 py-3 text-right font-medium">هزینه</th>
                <th className="px-4 py-3 text-right font-medium">ترتیب</th>
                <th className="px-4 py-3 text-right font-medium">نمایش در سفارش</th>
                <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                <th className="px-4 py-3 text-right font-medium">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row, index) => (
                <tr key={row.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-500">{rowNumber(index)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <Truck className="h-5 w-5" />
                      </span>
                      <div>
                        <div className="font-semibold text-slate-900">{row.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5" dir="ltr">
                          {row.slug}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{row.eta}</td>
                  <td className="px-4 py-3 text-slate-700">{priceLabel(row)}</td>
                  <td className="px-4 py-3 text-slate-600">{row.sortOrder}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        row.showInCheckout ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {row.showInCheckout ? 'بله' : 'خیر'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        row.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {row.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
                        title="ویرایش"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(row)}
                        className="rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش روش ارسال' : 'افزودن روش ارسال'}
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div>
            <label className={labelClass}>نام</label>
            <input className={fieldClass} value={form.name} onChange={(e) => setField('name', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>شناسه (انگلیسی)</label>
            <input
              className={fieldClass}
              value={form.slug}
              onChange={(e) => setField('slug', e.target.value)}
              dir="ltr"
              placeholder="post-pishtaz"
            />
            <p className="mt-1 text-xs text-slate-500">حروف کوچک انگلیسی، عدد و خط تیره. اگر خالی باشد خودکار ساخته می‌شود.</p>
          </div>
          <div>
            <label className={labelClass}>توضیح</label>
            <textarea
              className={fieldClass}
              rows={2}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>زمان تحویل</label>
              <input
                className={fieldClass}
                value={form.eta}
                onChange={(e) => setField('eta', e.target.value)}
                placeholder="۲ تا ۴ روز کاری"
              />
            </div>
            <NumberField
              label="ترتیب نمایش"
              value={String(form.sortOrder || 0)}
              onChange={(v) => setField('sortOrder', num(v))}
              grouped={false}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isFree}
              onChange={(e) => setField('isFree', e.target.checked)}
            />
            این روش همیشه رایگان است
          </label>
          {!form.isFree ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <NumberField
                label="هزینه ارسال (تومان)"
                value={String(form.cost || 0)}
                onChange={(v) => setField('cost', num(v))}
              />
              <NumberField
                label="ارسال رایگان از مبلغ (تومان)"
                value={String(form.freeThreshold || 0)}
                onChange={(v) => setField('freeThreshold', num(v))}
                placeholder="۰ = بدون سقف رایگان"
                hint="اگر صفر باشد، این روش هیچ‌وقت با سقف سبد رایگان نمی‌شود."
              />
            </div>
          ) : null}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
            />
            فعال (در فروشگاه نمایش داده شود)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.showInCheckout}
              onChange={(e) => setField('showInCheckout', e.target.checked)}
            />
            نمایش در صفحه ثبت سفارش
          </label>
        </div>
      </Modal>
    </div>
  );
}
